import { useEffect, useState } from 'react';

const CONTEXT_LENGTH = 200;
const SELECTION_DELAY_MS = 200;
const BUTTON_TEXT = 'Stel een wijziging voor';
const BUTTON_ICON = 'pencil-on-square';

export type SelectionSnapshot = {
  text: string;
  before: string;
  after: string;
  top: number;
  bottom: number;
  left: number;
};

export type SelectionScope = { selection: Selection | null; content: Element | null };

/** The selection as data: a Selection object is live and empties when a click lands elsewhere. */
function readSelection({ selection, content }: SelectionScope): SelectionSnapshot | undefined {
  if (!content || !selection || selection.isCollapsed || !selection.rangeCount) {
    return undefined;
  }
  const range = selection.getRangeAt(0).cloneRange();
  if (!content.contains(range.startContainer)) {
    return undefined;
  }
  // A triple click on the last paragraph ends in whatever follows the article.
  if (!content.contains(range.endContainer)) {
    range.setEnd(content, content.childNodes.length);
  }
  const text = range.toString().trim();
  if (!text) {
    return undefined;
  }
  const leading = document.createRange();
  leading.selectNodeContents(content);
  leading.setEnd(range.startContainer, range.startOffset);
  const trailing = document.createRange();
  trailing.selectNodeContents(content);
  trailing.setStart(range.endContainer, range.endOffset);
  const { top, bottom, left } = range.getBoundingClientRect();
  return {
    text,
    before: leading.toString().slice(-CONTEXT_LENGTH),
    after: trailing.toString().slice(0, CONTEXT_LENGTH),
    top,
    bottom,
    left,
  };
}

/** Follows the selection inside one article. `scope` must be stable, so wrap it in useCallback. */
export function useSelectionSnapshot(scope: () => SelectionScope, paused: boolean) {
  const [snapshot, setSnapshot] = useState<SelectionSnapshot>();

  useEffect(() => {
    if (paused) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout>;
    const update = () => setSnapshot(readSelection(scope()));
    const delayed = () => {
      clearTimeout(timer);
      timer = setTimeout(update, SELECTION_DELAY_MS);
    };
    document.addEventListener('selectionchange', delayed);
    window.addEventListener('scroll', update, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('selectionchange', delayed);
      window.removeEventListener('scroll', update, true);
    };
  }, [scope, paused]);

  return snapshot;
}

/** Sits at the selection. Render it in document.body, or an article can clip it. */
export function FloatingProposalButton({
  selection,
  onClick,
}: {
  selection: SelectionSnapshot;
  onClick: () => void;
}) {
  // Above the selection, or below it when that would fall off the screen.
  const top = selection.top > 56 ? selection.top - 48 : selection.bottom + 8;
  const left = Math.max(16, Math.min(selection.left, window.innerWidth - 240));
  return (
    // Keeps the selection alive: a mousedown on the button would otherwise clear it.
    <div
      style={{ position: 'fixed', zIndex: 1000, top, left }}
      onMouseDown={event => event.preventDefault()}
    >
      <nldd-button
        appearance="primary"
        size="sm"
        start-icon={BUTTON_ICON}
        text={BUTTON_TEXT}
        onClick={onClick}
      />
    </div>
  );
}

/** The same action without a selection, so it also works with the keyboard. */
export function PageProposalButton({ onClick }: { onClick: () => void }) {
  return (
    <nldd-button
      appearance="neutral-tinted"
      size="sm"
      start-icon={BUTTON_ICON}
      text={BUTTON_TEXT}
      onClick={onClick}
    />
  );
}
